// src/utils/authHeaders.js
//
// Attaches the signed-in user's Supabase access token to worker API calls
// (Identity Implementation Gate 1, resume-0040).
//
// The worker used to identify callers by an email in the request body, which
// meant any caller could name any user. It now verifies this token instead and
// derives the user from it, so these headers are what prove who is calling.
//
// The token is short-lived and refreshed by the Supabase client; always call
// this immediately before a request rather than caching the result.

import { supabase } from "../lib/supabaseClient";

/**
 * Returns { Authorization: "Bearer <token>" } for a signed-in user, or {} when
 * there is no session. Returning {} rather than throwing keeps callers simple:
 * the request still goes out and the worker answers 401, which is the correct
 * outcome for an unauthenticated caller.
 */
export async function getAuthHeaders() {
  if (!supabase) return {};

  try {
    const { data, error } = await supabase.auth.getSession();
    if (error) {
      console.warn("Could not read Supabase session for API call:", error);
      return {};
    }

    const token = data?.session?.access_token;
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch (err) {
    console.warn("Could not read Supabase session for API call:", err);
    return {};
  }
}

/**
 * Convenience for the common case of a JSON request that also needs auth.
 */
export async function jsonAuthHeaders(extra = {}) {
  return {
    "Content-Type": "application/json",
    ...(await getAuthHeaders()),
    ...extra,
  };
}
